/* eslint-disable camelcase */
import dayjs from 'dayjs';

import { useState, useEffect } from 'react';

import { Col, DatePicker, Input, Row, Select, Space, Typography } from 'antd';

import * as Constants from '../../../constants';
import { CommonModalFooter, ModalWrapper } from '../../Common';

const { Text, Title } = Typography;
const { TextArea } = Input;

const FM_STATUS_OPTIONS = [
  { label: 'Picked', value: 'PICKED' },
  { label: 'Not Picked', value: 'NOT_PICKED' },
  { label: 'Disposed', value: 'DISPOSED' }
];

const LM_STATUS_OPTIONS = [
  { label: 'In Transit', value: 'IN_TRANSIT' },
  { label: 'Arrived at LM', value: 'ARRIVED_AT_LM' },
  { label: 'Out for delivery', value: 'OUT_FOR_DELIVERY' },
  { label: 'Reattempt', value: 'REATTEMPT' }
];

const LmDateModal = ({ open, onClose, onSubmit, loading, initialData, transitions = [] }) => {
  const [lmDateData, setLmDateData] = useState({
    // FM Date Section
    fm_actual_date: null,
    fm_expected_date: null,
    fm_status: null,
    fm_remarks: '',
    // LM Arrival Date Expected Section
    lm_expected_date: null,
    lm_expected_status: null,
    expected_out_for_delivery_date: null,
    lm_expected_remarks: '',
    // LM Arrival Date Actual Section
    lm_actual_date: null,
    lm_actual_status: null,
    actual_out_for_delivery_date: null,
    lm_actual_remarks: ''
  });

  // Reset form data when modal closes
  useEffect(() => {
    if (open) {
      setLmDateData({
        fm_actual_date: initialData?.fm_actual_date || null,
        fm_expected_date: initialData?.fm_expected_date || null,
        fm_status: initialData?.fm_status || null,
        fm_remarks: initialData?.fm_remarks || '',
        lm_expected_date: initialData?.lm_expected_date || null,
        lm_expected_status: initialData?.lm_expected_status || null,
        expected_out_for_delivery_date: initialData?.expected_out_for_delivery_date || null,
        lm_expected_remarks: initialData?.lm_expected_remarks || '',
        lm_actual_date: initialData?.lm_actual_date || null,
        lm_actual_status: initialData?.lm_actual_status || null,
        actual_out_for_delivery_date: initialData?.actual_out_for_delivery_date || null,
        lm_actual_remarks: initialData?.lm_actual_remarks || ''
      });
    } else {
      setLmDateData({
        fm_actual_date: null,
        fm_expected_date: null,
        fm_status: null,
        fm_remarks: '',
        lm_expected_date: null,
        lm_expected_status: null,
        expected_out_for_delivery_date: null,
        lm_expected_remarks: '',
        lm_actual_date: null,
        lm_actual_status: null,
        actual_out_for_delivery_date: null,
        lm_actual_remarks: ''
      });
    }
  }, [open, initialData]);

  const handleCancel = () => {
    onClose();
  };

  const handleSubmit = () => {
    onSubmit(lmDateData);
  };

  // Check which sections should be shown based on transitions
  const showFmSection = transitions.includes('RO_UPDATE_FM_DATE');
  const showLmSection = transitions.includes('RO_UPDATE_LM_DATE');

  // Validation function
  const isValidForSubmit = () => {
    // Check if any date is added in each section
    const hasFmDate = lmDateData.fm_actual_date || lmDateData.fm_expected_date;
    const hasLmExpectedDate = lmDateData.lm_expected_date || lmDateData.expected_out_for_delivery_date;
    const hasLmActualDate = lmDateData.lm_actual_date || lmDateData.actual_out_for_delivery_date;

    // If any date is added in a section, status for that section is mandatory
    if (hasFmDate && !lmDateData.fm_status) {
      return false;
    }
    if (hasLmExpectedDate && !lmDateData.lm_expected_status) {
      return false;
    }
    if (hasLmActualDate && !lmDateData.lm_actual_status) {
      return false;
    }

    // Check if any data exists in each section
    const hasFmData = hasFmDate || lmDateData.fm_status || lmDateData.fm_remarks;
    const hasLmExpected = hasLmExpectedDate || lmDateData.lm_expected_status || lmDateData.lm_expected_remarks;
    const hasLmActual = hasLmActualDate || lmDateData.lm_actual_status || lmDateData.lm_actual_remarks;

    // At least one field from any visible section must be filled
    if (showFmSection && showLmSection) {
      return hasFmData || hasLmExpected || hasLmActual;
    }
    if (showFmSection) {
      return hasFmData;
    }
    if (showLmSection) {
      return hasLmExpected || hasLmActual;
    }
    return false;
  };

  // Render FM Date Section
  const renderFmDateSection = () => (
    <Space className="w-100" direction="vertical">
      <Title level={5}>FM Date</Title>

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Text className="fw-500">FM Actual Date</Text>
          <DatePicker
            className="w-100"
            format={Constants.DATE_FORMAT}
            onChange={(date) => setLmDateData((prev) => ({ ...prev, fm_actual_date: date?.toISOString() }))}
            value={lmDateData.fm_actual_date ? dayjs(lmDateData.fm_actual_date) : null}
          />
        </Col>
        <Col span={12}>
          <Text className="fw-500">FM Expected Date</Text>
          <DatePicker
            className="w-100"
            format={Constants.DATE_FORMAT}
            onChange={(date) => setLmDateData((prev) => ({ ...prev, fm_expected_date: date?.toISOString() }))}
            value={lmDateData.fm_expected_date ? dayjs(lmDateData.fm_expected_date) : null}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text className="fw-500">FM Status</Text>
          <Select
            className="w-100"
            onChange={(value) => setLmDateData((prev) => ({ ...prev, fm_status: value }))}
            options={FM_STATUS_OPTIONS}
            placeholder="Select FM Status"
            value={lmDateData.fm_status}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text className="fw-500">FM Remarks</Text>
          <TextArea
            onChange={(e) => setLmDateData((prev) => ({ ...prev, fm_remarks: e.target.value }))}
            rows={3}
            value={lmDateData.fm_remarks}
          />
        </Col>
      </Row>
    </Space>
  );

  // Render LM Arrival Date Expected Section
  const renderLmExpectedSection = () => (
    <Space className="w-100" direction="vertical" style={{ marginTop: '24px' }}>
      <Title level={5}>LM Arrival Date Expected</Title>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text className="fw-500">LM Expected Date</Text>
          <DatePicker
            className="w-100"
            format={Constants.DATE_FORMAT}
            onChange={(date) => setLmDateData((prev) => ({ ...prev, lm_expected_date: date?.toISOString() }))}
            value={lmDateData.lm_expected_date ? dayjs(lmDateData.lm_expected_date) : null}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Text className="fw-500">LM Status</Text>
          <Select
            className="w-100"
            onChange={(value) => setLmDateData((prev) => ({ ...prev, lm_expected_status: value }))}
            options={LM_STATUS_OPTIONS}
            placeholder="Select LM Status"
            value={lmDateData.lm_expected_status}
          />
        </Col>
        <Col span={12}>
          <Text className="fw-500">Out for Delivery Date</Text>
          <DatePicker
            className="w-100"
            disabled={
              !lmDateData.lm_expected_status ||
              !['OUT_FOR_DELIVERY', 'REATTEMPT'].includes(lmDateData.lm_expected_status)
            }
            format={Constants.DATE_FORMAT}
            onChange={(date) =>
              setLmDateData((prev) => ({ ...prev, expected_out_for_delivery_date: date?.toISOString() }))
            }
            value={lmDateData.expected_out_for_delivery_date ? dayjs(lmDateData.expected_out_for_delivery_date) : null}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text className="fw-500">LM Remarks</Text>
          <TextArea
            onChange={(e) => setLmDateData((prev) => ({ ...prev, lm_expected_remarks: e.target.value }))}
            rows={3}
            value={lmDateData.lm_expected_remarks}
          />
        </Col>
      </Row>
    </Space>
  );

  // Render LM Arrival Date Actual Section
  const renderLmActualSection = () => (
    <Space className="w-100" direction="vertical" style={{ marginTop: '24px' }}>
      <Title level={5}>LM Arrival Date Actual</Title>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text className="fw-500">LM Actual Date</Text>
          <DatePicker
            className="w-100"
            format={Constants.DATE_FORMAT}
            onChange={(date) => setLmDateData((prev) => ({ ...prev, lm_actual_date: date?.toISOString() }))}
            value={lmDateData.lm_actual_date ? dayjs(lmDateData.lm_actual_date) : null}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Text className="fw-500">LM Status</Text>
          <Select
            className="w-100"
            onChange={(value) => setLmDateData((prev) => ({ ...prev, lm_actual_status: value }))}
            options={LM_STATUS_OPTIONS}
            placeholder="Select LM Status"
            value={lmDateData.lm_actual_status}
          />
        </Col>
        <Col span={12}>
          <Text className="fw-500">Out for Delivery Date</Text>
          <DatePicker
            className="w-100"
            disabled={
              !lmDateData.lm_actual_status || !['OUT_FOR_DELIVERY', 'REATTEMPT'].includes(lmDateData.lm_actual_status)
            }
            format={Constants.DATE_FORMAT}
            onChange={(date) =>
              setLmDateData((prev) => ({ ...prev, actual_out_for_delivery_date: date?.toISOString() }))
            }
            value={lmDateData.actual_out_for_delivery_date ? dayjs(lmDateData.actual_out_for_delivery_date) : null}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text className="fw-500">LM Remarks</Text>
          <TextArea
            onChange={(e) => setLmDateData((prev) => ({ ...prev, lm_actual_remarks: e.target.value }))}
            rows={3}
            value={lmDateData.lm_actual_remarks}
          />
        </Col>
      </Row>
    </Space>
  );

  // Render modal content based on transitions
  const renderModalContent = () => {
    return (
      <Space className="w-100" direction="vertical">
        {showFmSection ? renderFmDateSection() : null}
        {showLmSection ? renderLmExpectedSection() : null}
        {showLmSection ? renderLmActualSection() : null}
      </Space>
    );
  };

  // Don't render modal if no valid transitions
  if (!showFmSection && !showLmSection) {
    return null;
  }

  return (
    <ModalWrapper
      content={renderModalContent()}
      footer={
        <CommonModalFooter
          ifSubmitDisabled={loading || !isValidForSubmit()}
          loading={loading}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          submitButtonLabel="Submit"
        />
      }
      onCancel={handleCancel}
      open={open}
      title="Add LM Date"
    />
  );
};

export default LmDateModal;
